import type { ComponentProps } from "react";

import { SearchLg } from "@untitledui/icons";
import { forwardRef, useImperativeHandle, useRef } from "react";

import { CloseButton } from "@/components/ui/buttons/close-button";
import { Input } from "@/components/ui/input/input";
import { cx } from "@/utils/cx";

export type SearchInputWithClearHandle = {
	clearButton: HTMLButtonElement | null;
	input: HTMLInputElement | null;
};

export type SearchInputWithClearProps = Omit<
	ComponentProps<typeof Input>,
	"className" | "icon" | "onChange" | "value"
> & {
	/** Applied to the outer wrapper (search icon + field + clear control). */
	className?: string;
	/** Shown when the input has any characters (including spaces). */
	clearButtonLabel?: string;
	/** Test id on the outer wrapper; use a descendant selector to target the input. */
	"data-testid"?: string;
	includeSearchIcon?: boolean;
	/** Called before the clear action runs (pointer or keyboard). */
	onBeforeClear?: () => void;
	onChange: (value: string) => void;
	/**
	 * Optional handler when the clear control is pressed. Defaults to
	 * `onChange("")`.
	 */
	onClear?: () => void;
	value: string;
};

/**
 * Search field with leading search icon and a clear (X) control when the value
 * is non-empty. Adds trailing padding so text does not overlap the clear button.
 *
 * Exposes `input` and `clearButton` refs for parent keyboard/focus wiring.
 */
export const SearchInputWithClear = forwardRef<
	SearchInputWithClearHandle,
	SearchInputWithClearProps
>(
	(
		{
			className,
			clearButtonLabel = "Clear search",
			"data-testid": dataTestId,
			includeSearchIcon = true,
			isDisabled,
			onBeforeClear,
			onChange,
			onClear,
			value,
			...props
		},
		forwardedRef,
	) => {
		const inputRef = useRef<HTMLInputElement>(null);
		const clearButtonRef = useRef<HTMLButtonElement>(null);
		const showClearButton = value.length > 0 && !isDisabled;

		useImperativeHandle(
			forwardedRef,
			() => ({
				get clearButton() {
					return clearButtonRef.current;
				},
				get input() {
					return inputRef.current;
				},
			}),
			[],
		);

		const handleClearIntent = () => {
			onBeforeClear?.();
		};

		const handleClear = () => {
			// Custom clear is used for additional logic (e.g. clearing debounced search query).
			// It bypasses default onChange("") to prevent the input value from being reset.
			if (onClear) {
				onClear();
			} else {
				onChange("");
			}
		};

		return (
			<div
				className={cx("relative w-full", className)}
				data-testid={dataTestId}
			>
				<Input
					{...props}
					className="w-full [&_input]:pr-10"
					icon={includeSearchIcon ? SearchLg : undefined}
					isDisabled={isDisabled}
					onChange={onChange}
					ref={inputRef}
					value={value}
				/>
				{showClearButton && (
					<CloseButton
						className={({ isFocusVisible }) =>
							cx(
								"absolute top-1/2 right-2 z-10 -translate-y-1/2 rounded-md",
								isFocusVisible && "ring-2 ring-brand",
							)
						}
						label={clearButtonLabel}
						onPointerDown={(event) => {
							event.preventDefault();
							handleClearIntent();
						}}
						onPress={handleClear}
						onPressStart={handleClearIntent}
						ref={clearButtonRef}
						size="xs"
					/>
				)}
			</div>
		);
	},
);

SearchInputWithClear.displayName = "SearchInputWithClear";
